import React, { useState } from "react";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { useGetAutoDelegationStatsQuery, useGetAutoDelegationHistoryQuery } from "../../utils/api/endpoints/delegationsApi";
import { format } from "date-fns";
import axios from "../../utils/api/axios";
import AutoDelegateIcon from "../../assets/icons/auto-delegate.svg";
import ReportDelegateIcon from "../../assets/icons/report-delegate.svg";
import ReportDelegateBlueIcon from "../../assets/icons/report-del-blue.svg";
import RetestDelegateGreenIcon from "../../assets/icons/retest-dele-green.svg";
import EscalationDelegateIcon from "../../assets/icons/escalation-delegate.svg";
import FailureDelegateIcon from "../../assets/icons/failure-delegate.svg";
import TriggerIcon from "../../assets/icons/trigger-btn.svg";
import ScannedIcon from "../../assets/icons/scanned-dele.svg";
import DelegatedIcon from "../../assets/icons/delaged-dele.svg";
import SkippedIcon from "../../assets/icons/skipped-dele.svg";
import FailedIcon from "../../assets/icons/failed-dele.svg";

interface AutoDelegationRecord {
  id: string;
  type: "retest" | "report_comment";
  object_id: number;
  from_user_id: number;
  to_user_id: number;
  from_user_avatar?: string | null;
  to_user_avatar?: string | null;
  from_user_profile_picture?: string | null;
  to_user_profile_picture?: string | null;
  from_user_pfp?: string | null;
  to_user_pfp?: string | null;
  status: "pending" | "accepted" | "rejected" | "expired" | "revoked";
  attempt: number;
  created_at: string;
  program_id: number | null;
  report_title: string | null;
  from_user_name: string;
  to_user_name: string;
  is_deleted?: boolean;
  deleted_at?: string | null;
  deleted_by_user?: {
    user_id: number;
    username: string;
    display_name: string;
  } | null;
}

const AutoDelegationMonitoring = () => {
  usePageTitle("Auto-Delegation Monitoring");
  const [filter, setFilter] = useState<"all" | "retest" | "report_comment">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "accepted" | "rejected" | "expired" | "revoked">("all");
  const [triggering, setTriggering] = useState<boolean>(false);
  const [triggerMessage, setTriggerMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const { data: stats, isLoading: statsLoading, refetch: refetchStats, error: statsError } = useGetAutoDelegationStatsQuery();
  const { data: history, isLoading: historyLoading, refetch: refetchHistory, error: historyError } = useGetAutoDelegationHistoryQuery({
    type: filter === "all" ? undefined : filter,
    status: statusFilter === "all" ? undefined : statusFilter,
    limit: 100
  });

  // Debug logging
  React.useEffect(() => {
    console.log("Stats data:", stats);
    console.log("Stats error:", statsError);
    console.log("History data:", history);
    console.log("History error:", historyError);
  }, [stats, statsError, history, historyError]);

  const handleTriggerAutoDelegation = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      const response = await axios.post("/v2/delegations/auto/trigger?type=all");

      if (response.data) {
        setTriggerMessage({
          type: "success",
          text: response.data.message || "Auto-delegation triggered successfully!"
        });
        // Refetch data after triggering with a longer delay to allow processing
        setTimeout(() => {
          refetchStats();
          refetchHistory();
        }, 2000);
      }
    } catch (error: any) {
      console.error("Error triggering auto-delegation:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to trigger auto-delegation";
      setTriggerMessage({ type: "error", text: errorMessage });
    } finally {
      setTriggering(false);
      // Clear message after 5 seconds
      setTimeout(() => setTriggerMessage(null), 5000);
    }
  };

  const filteredHistory = history?.data || [];

  const calcRate = (success?: number, total?: number) => {
    if (!total || total <= 0 || !success) return null;
    const rate = Math.min(100, Math.round((success / total) * 100));
    return `${rate}%`;
  };

  const getStatusBadge = (status: string, isDeleted?: boolean) => {
    if (isDeleted) {
      return "bg-red-100 text-red-700";
    }
    const badges = {
      pending: "bg-ctb-orange-100 text-ctb-orange-200",
      accepted: "bg-[#D1FAE5] text-ctb-green-300",
      rejected: "bg-ctb-red-50 text-ctb-red-400",
      expired: "bg-ctb-yellow-300 text-ctb-yellow-250",
      revoked: "bg-ctb-grey-150 text-ctb-grey-800"
    };
    return badges[status as keyof typeof badges] || "bg-ctb-grey-50 text-ctb-grey-600";
  };

  const getTypeBadge = (type: string) => {
    return type === "retest" ? "bg-ctb-blue-150 text-ctb-blue-200" : "bg-ctb-blue-0 text-ctb-blue-400";
  };

  const getAvatarSrc = (record: AutoDelegationRecord, key: "from" | "to") => {
    const map = {
      from: [record.from_user_avatar, record.from_user_profile_picture, record.from_user_pfp],
      to: [record.to_user_avatar, record.to_user_profile_picture, record.to_user_pfp]
    };
    return map[key].find(Boolean) as string | undefined;
  };

  const renderAvatar = (name: string, src?: string) => {
    const initial = name?.trim()?.[0]?.toUpperCase() || "?";
    return (
      src ? (
        <img src={src} alt={name} className="h-7 w-7 rounded-full object-cover border border-gray-200" />
      ) : (
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-ctb-blue-150 text-ctb-blue-400 text-xs font-semibold">
          {initial}
        </span>
      )
    );
  };

  return (
    <div className="montserrat-font min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <div className="max-w-7xl mx-auto" role="main">
        <>
          {/* Compact Header */}
          <div className="mb-6 flex justify-between items-center bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-ctb-green-200 flex items-center justify-center shadow-inner">
                <img src={AutoDelegateIcon} alt="Auto delegation icon" className="h-6 w-6" />
              </div>
              <div className="space-y-1">
                <h1 className="text-xl font-semibold text-gray-900">Auto-Delegation Monitor</h1>
                <p className="text-sm text-gray-600">Real-time delegation tracking &amp; analytics</p>
              </div>
            </div>
            <button
              onClick={handleTriggerAutoDelegation}
              disabled={triggering}
              className="px-4 py-3 bg-[#010D2C] text-white text-xs font-medium rounded-lg hover:bg-[#02153f] disabled:bg-ctb-grey-700 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
            >
              {triggering ? (
                <>
                  <span className="animate-spin">⚙️</span>
                  Triggering...
                </>
              ) : (
                <>
                  <img src={TriggerIcon} alt="Trigger icon" className="h-4 w-4" />
                  Trigger Now
                </>
              )}
            </button>
          </div>

        {/* Trigger Message Display */}
        {triggerMessage && (
          <div className={`mb-4 p-3 border rounded-lg flex items-center gap-2 ${
            triggerMessage.type === "success"
              ? "bg-[#D1FAE5] border-ctb-green-300"
              : "bg-ctb-red-50 border-ctb-red-400"
          }`}>
            <span className={triggerMessage.type === "success" ? "text-ctb-green-300" : "text-ctb-red-400"}>
              {triggerMessage.type === "success" ? "✅" : "⚠️"}
            </span>
            <p className={`text-sm ${triggerMessage.type === "success" ? "text-ctb-green-500" : "text-ctb-red-400"}`}>
              {triggerMessage.text}
            </p>
          </div>
        )}

        {/* Error Display */}
        {statsError && (
          <div className="mb-4 p-3 bg-ctb-red-50 border border-ctb-red-400 rounded-lg flex items-center gap-2">
            <span className="text-ctb-red-400">⚠️</span>
            <p className="text-sm text-ctb-red-400">Error loading statistics: {String((statsError as any)?.message || "Unknown error")}</p>
          </div>
        )}

        {/* Compact Stats Grid */}
        {statsLoading ? (
          <div className="text-center py-6 bg-white rounded-xl shadow-sm">
            <div className="animate-pulse flex items-center justify-center gap-2">
              <span className="text-2xl">⏳</span>
              <span className="text-gray-600">Loading statistics...</span>
            </div>
          </div>
        ) : stats ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="relative rounded-xl bg-white shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-black">Retests</p>
                <div className="h-10 w-10 rounded-lg bg-ctb-green-200 flex items-center justify-center shadow-sm">
                  <img src={AutoDelegateIcon} alt="Retests icon" className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-semibold text-black">{stats.retests_delegated ?? 0}</p>
                <p className="text-xs text-ctb-grey-600 mt-1">Active delegations</p>
              </div>
            </div>

            <div className="relative rounded-xl bg-white shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-black">Reports</p>
                <div className="h-10 w-10 rounded-lg bg-ctb-blue-100 flex items-center justify-center shadow-sm">
                  <img src={ReportDelegateIcon} alt="Reports icon" className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-semibold text-black">{stats.reports_delegated ?? 0}</p>
                <p className="text-xs text-ctb-grey-600 mt-1">Pending reports</p>
              </div>
            </div>

            <div className="relative rounded-xl bg-white shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-black">Escalations</p>
                <div className="h-10 w-10 rounded-lg bg-[#EAEFFD] flex items-center justify-center shadow-sm">
                  <img src={EscalationDelegateIcon} alt="Escalations icon" className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-semibold text-black">{stats.total_escalations ?? 0}</p>
                <p className="text-xs text-ctb-grey-600 mt-1">Escalated items</p>
              </div>
            </div>

            <div className="relative rounded-xl bg-white shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-black">Failures</p>
                <div className="h-10 w-10 rounded-lg bg-ctb-red-50 flex items-center justify-center shadow-sm">
                  <img src={FailureDelegateIcon} alt="Failures icon" className="h-5 w-5" />
                </div>
              </div>
              <div className="mt-3">
                <p className="text-3xl font-semibold text-black">
                  {(stats.retests_failed ?? 0) + (stats.reports_failed ?? 0)}
                </p>
                <p className="text-xs text-ctb-grey-600 mt-1">Failed attempts</p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Compact Detailed Stats */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-0 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 bg-[#F8FAFC] border-b border-gray-200">
                <div className="h-10 w-10 rounded-lg bg-[#D1FAE5] flex items-center justify-center shadow-sm">
                  <img src={RetestDelegateGreenIcon} alt="Retest delegation icon" className="h-5 w-5" />
                </div>
                <h2 className="text-base font-semibold text-black">Retest Delegation</h2>
              </div>
              <div className="px-5 py-4 space-y-10">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={ScannedIcon} alt="Scanned" className="h-4 w-4" />
                    <span>Scanned</span>
                  </div>
                  <span className="text-sm font-semibold text-black">{stats.total_retests_scanned ?? 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={DelegatedIcon} alt="Delegated" className="h-4 w-4" />
                    <span>Delegated</span>
                  </div>
                  <span className="text-sm font-semibold text-ctb-green-300">{stats.retests_delegated ?? 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={SkippedIcon} alt="Skipped" className="h-4 w-4" />
                    <span>Skipped</span>
                  </div>
                  <span className="text-sm font-semibold text-ctb-yellow-250">{stats.retests_skipped ?? 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={FailedIcon} alt="Failed" className="h-4 w-4" />
                    <span>Failed</span>
                  </div>
                  <span className="text-sm font-semibold text-ctb-red-400">{stats.retests_failed ?? 0}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-ctb-grey-600 mb-1">
                    <span>Success Rate</span>
                    <span>{calcRate(stats.retests_delegated, stats.total_retests_scanned) ?? "N/A"}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-ctb-green-300 transition-all"
                      style={{ width: calcRate(stats.retests_delegated, stats.total_retests_scanned) ?? "0%" }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-0 shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-4 bg-[#F8FAFC] border-b border-gray-200">
                <div className="h-10 w-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center shadow-sm">
                  <img src={ReportDelegateBlueIcon} alt="Report delegation icon" className="h-5 w-5" />
                </div>
                <h2 className="text-base font-semibold text-black">Report Delegation</h2>
              </div>
              <div className="px-5 py-4 space-y-10">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={ScannedIcon} alt="Scanned" className="h-4 w-4" />
                    <span>Scanned</span>
                  </div>
                  <span className="text-sm font-semibold text-black">{stats.total_reports_scanned ?? 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={DelegatedIcon} alt="Delegated" className="h-4 w-4" />
                    <span>Delegated</span>
                  </div>
                  <span className="text-sm font-semibold text-ctb-green-300">{stats.reports_delegated ?? 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={SkippedIcon} alt="Skipped" className="h-4 w-4" />
                    <span>Skipped</span>
                  </div>
                  <span className="text-sm font-semibold text-ctb-yellow-250">{stats.reports_skipped ?? 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <img src={FailedIcon} alt="Failed" className="h-4 w-4" />
                    <span>Failed</span>
                  </div>
                  <span className="text-sm font-semibold text-ctb-red-400">{stats.reports_failed ?? 0}</span>
                </div>
                <div className="pt-2 border-t border-gray-200">
                  <div className="flex items-center justify-between text-xs text-ctb-grey-600 mb-1">
                    <span>Success Rate</span>
                    <span>{calcRate(stats.reports_delegated, stats.total_reports_scanned) ?? "N/A"}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full bg-ctb-blue-200 transition-all"
                      style={{ width: calcRate(stats.reports_delegated, stats.total_reports_scanned) ?? "0%" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="bg-[#010D2C] text-white px-6 py-6 flex flex-wrap items-center gap-6">
            <span className="text-sm font-medium flex items-center gap-2">
              <span>Filters:</span>
            </span>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-white/80">Type</span>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as any)}
                className="px-4 py-2 rounded-lg bg-transparent border border-white/30 text-white text-sm focus:outline-none"
              >
                <option value="all">All Types</option>
                <option value="retest">Retest</option>
                <option value="report_comment">Report Comment</option>
              </select>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-medium text-white/80">Status</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="px-4 py-2 rounded-lg bg-transparent border border-white/30 text-white text-sm focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="expired">Expired</option>
                <option value="revoked">Revoked</option>
              </select>
            </div>
            <span className="text-xs text-white/80 ml-auto">
              {filteredHistory.length} {filteredHistory.length === 1 ? "record" : "records"}
            </span>
          </div>

          <div className="px-5 py-5 border-b border-gray-200 bg-[#F8FAFC]">
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-3">
              <img src={ReportDelegateBlueIcon} alt="Delegation history icon" className="h-5 w-5" />
              <span>Delegation History</span>
            </h2>
          </div>

          {historyLoading ? (
            <div className="text-center py-8">
              <div className="animate-pulse flex items-center justify-center gap-2">
                <span className="text-2xl">⏳</span>
                <span className="text-gray-600">Loading history...</span>
              </div>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-4xl mb-2 block">📭</span>
              <p className="text-gray-500 text-sm">No delegations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F8FAFC] border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Module</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Report Title</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">From</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">To</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-700 uppercase tracking-wide">Attempt</th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredHistory.map((record: AutoDelegationRecord) => (
                    <tr key={record.id} className="hover:bg-ctb-blue-150 transition-colors">
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-2 px-2 py-1 rounded-md text-xs font-medium bg-ctb-blue-150 text-ctb-blue-400">
                          <img
                            src={record.type === "retest" ? RetestDelegateGreenIcon : ReportDelegateBlueIcon}
                            alt={record.type === "retest" ? "Retest" : "Report"}
                            className="h-4 w-4"
                          />
                          {record.type === "retest" ? "Retest" : "Report"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {record.report_title || `Report #${record.object_id}`}
                          </span>
                          <span className="text-xs text-gray-500">
                            {record.type === "retest" ? `Retest #${record.object_id}` : `Report #${record.object_id}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          {renderAvatar(record.from_user_name, getAvatarSrc(record, "from"))}
                          <span>{record.from_user_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                          {renderAvatar(record.to_user_name, getAvatarSrc(record, "to"))}
                          <span>{record.to_user_name}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 rounded-md text-xs font-medium ${getStatusBadge(record.status, record.is_deleted || !!record.deleted_at)}`}>
                          {record.is_deleted || record.deleted_at
                            ? `Deleted (was ${record.status.charAt(0).toUpperCase() + record.status.slice(1)})`
                            : record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-xs font-semibold text-gray-700">
                          {record.attempt}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-xs text-gray-600">
                        {format(new Date(record.created_at), "MMM dd, HH:mm")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      </div>
    </div>
  );
};

export default AutoDelegationMonitoring;

